import React, { useRef, useState } from "react";
import { colors, fonts, shadows } from "../theme";
import { compassFromDeg, haversineKm } from "../lib/weather";

const EPW_MISMATCH_KM = 50;
const SKELETON_KEYS = ["dli", "temp", "hum", "ghi", "dni", "wind", "precip", "gdd", "frost"];

function sourceColor(source) {
  if (source === "epw") return colors.pillar.resources;
  if (source === "estimate") return colors.warn;
  return colors.pillar.location;
}

function sourceLabel(source) {
  if (source === "epw") return "EnergyPlus (.epw) climate override";
  if (source === "estimate") return "Estimate · live weather unavailable";
  return "Live fetch · Open-Meteo";
}

export default function WeatherPanel({ weather, loading, onEpwFile, onEpwClear, geo }) {
  const stationDist =
    weather?.source === "epw" && weather.stationLat != null && geo?.lat != null
      ? haversineKm(geo, { lat: weather.stationLat, lng: weather.stationLng })
      : null;
  const epwMismatch = stationDist != null && stationDist > EPW_MISMATCH_KM;

  let content;
  if (loading) {
    content = (
      <div style={styles.grid} data-testid="weather-loading">
        {SKELETON_KEYS.map((k) => (
          <div key={k} style={styles.skeleton} />
        ))}
      </div>
    );
  } else if (weather) {
    content = (
      <div data-testid="weather-data">
        <div style={styles.grid}>
          <Tile label="DLI" full="Daily Light Integral" value={weather.dli} unit="mol/m²·d" accent={colors.pillar.crops} tip="Measures total photosynthetically active radiation over 24 hours. Crucial for determining crop viability." />
          <Tile label="TEMP" full="Air Temperature" value={weather.temperature} unit="°C" accent={colors.pillar.systems} tip="Average dry-bulb air temperature at the site." />
          <Tile label="HUMIDITY" full="Relative Humidity" value={weather.humidity} unit="%" accent={colors.pillar.location} tip="Average relative humidity — affects transpiration and disease pressure." />
          <Tile label="GHI" full="Global Horizontal Irradiance" value={weather.ghi} unit="kWh/m²·d" accent={colors.pillar.systems} tip="Solar energy metric used for energy generation and glazing calculations." />
          <Tile label="DNI" full="Direct Normal Irradiance" value={weather.dni} unit="W/m²" accent={colors.pillar.systems} tip="Solar energy metric used for energy generation and glazing calculations." />
          <Tile label="WIND" full="Wind Speed & Direction" value={weather.windSpeed} unit={`km/h ${compassFromDeg(weather.windDir)}`} accent={colors.pillar.resources} tip="Average wind speed and dominant direction — informs ventilation and structural loads." />
          <Tile label="PRECIP" full="Annual Precipitation" value={weather.precip} unit="mm/yr" accent={colors.pillar.location} tip="Estimated annual precipitation — informs rainwater harvesting potential." />
          <Tile label="GDD" full="Growing Degree Days" value={weather.gdd} unit="base10" accent={colors.pillar.crops} tip="Heat accumulation metric used to predict plant development rates." />
          <Tile label="FROST-FREE" full="Frost-Free Season" value={weather.frostFree} unit="days/yr" accent={colors.pillar.crops} tip="Estimated frost-free days per year — bounds the outdoor growing season." />
        </div>
        <div style={styles.badge}>
          <span style={{ ...styles.dot, background: sourceColor(weather.source) }} />
          {sourceLabel(weather.source)}
        </div>
      </div>
    );
  } else {
    content = null;
  }

  return (
    <div>
      {content}

      {epwMismatch && (
        <div style={styles.mismatch} data-testid="epw-mismatch-warning">
          <span style={styles.mismatchIcon}>⚠</span>
          <span>
            The .epw weather station{weather.stationName ? ` (${weather.stationName})` : ""} is{" "}
            <strong>{stationDist} km</strong> from your pinned site. Climate data may not represent
            your location — verify the file matches your project site.
          </span>
        </div>
      )}

      <EpwZone onEpwFile={onEpwFile} onEpwClear={onEpwClear} active={weather?.source === "epw"} />
    </div>
  );
}

function Tile({ label, full, value, unit, accent, tip }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      style={styles.tile}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid={`climate-tile-${label.toLowerCase()}`}
    >
      <div style={styles.tileLabelRow}>
        <span style={{ ...styles.tileLabel, color: accent }}>{label}</span>
        {tip && <span style={styles.tileInfo}>ⓘ</span>}
      </div>
      <div style={styles.tileValue}>{value ?? "—"}</div>
      <div style={styles.tileUnit}>{unit}</div>
      {full && <div style={styles.tileFull}>{full}</div>}
      {hover && tip && (
        <div style={styles.tooltip} data-testid={`climate-tooltip-${label.toLowerCase()}`}>
          <div style={styles.tooltipTitle}>{full} ({label})</div>
          <div style={styles.tooltipBody}>{tip}</div>
        </div>
      )}
    </div>
  );
}

function EpwZone({ onEpwFile, onEpwClear, active }) {
  const inputRef = useRef(null);
  const drop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f && f.name.toLowerCase().endsWith(".epw")) onEpwFile(f);
  };
  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => e.preventDefault()}
      onDrop={drop}
      style={{
        ...styles.epw,
        borderColor: active ? colors.pillar.resources : colors.borderStrong,
        background: active ? "rgba(0,199,190,0.06)" : "rgba(0,0,0,0.015)",
      }}
      data-testid="epw-dropzone"
    >
      <span style={styles.epwIcon}>⤓</span>
      <span style={styles.epwText}>
        {active ? "Climate overridden by .epw (location unchanged)" : "Override climate with an .epw file"}
      </span>
      {active && onEpwClear && (
        <button
          type="button"
          data-testid="epw-clear-btn"
          title="Remove the .epw override and revert to live climate for this site"
          onClick={(e) => { e.stopPropagation(); onEpwClear(); }}
          style={styles.epwClear}
        >
          Clear
        </button>
      )}
      <input ref={inputRef} type="file" accept=".epw" onChange={(e) => e.target.files?.[0] && onEpwFile(e.target.files[0])} style={{ display: "none" }} />
    </div>
  );
}

const styles = {
  grid: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 },
  tile: { position: "relative", padding: "10px 11px", borderRadius: 12, background: "#fff", border: `1px solid ${colors.border}`, boxShadow: shadows.soft, cursor: "help" },
  tileLabelRow: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  tileLabel: { fontFamily: fonts.mono, fontSize: 8.5, letterSpacing: 1, fontWeight: 600 },
  tileInfo: { fontSize: 8.5, color: colors.textTertiary },
  tileValue: { fontFamily: fonts.sans, fontSize: 20, fontWeight: 700, letterSpacing: -0.5, color: colors.textPrimary, marginTop: 3, lineHeight: 1 },
  tileUnit: { fontFamily: fonts.mono, fontSize: 8.5, color: colors.textTertiary, marginTop: 3 },
  tileFull: { fontFamily: fonts.sans, fontSize: 8.5, color: colors.textSecondary, marginTop: 5, lineHeight: 1.2 },
  tooltip: { position: "absolute", left: 0, top: "calc(100% + 6px)", width: 200, padding: "10px 12px", borderRadius: 10, background: colors.textPrimary, color: "#fff", boxShadow: shadows.float, zIndex: 50, pointerEvents: "none" },
  tooltipTitle: { fontFamily: fonts.sans, fontSize: 11.5, fontWeight: 700, marginBottom: 4 },
  tooltipBody: { fontFamily: fonts.sans, fontSize: 11, lineHeight: 1.45, opacity: 0.9 },
  skeleton: { height: 62, borderRadius: 12, background: "linear-gradient(90deg,#eee,#f6f6f8,#eee)", backgroundSize: "200% 100%", animation: "bia-shimmer 1.2s infinite" },
  badge: { marginTop: 10, display: "flex", alignItems: "center", gap: 6, fontFamily: fonts.sans, fontSize: 11.5, color: colors.textSecondary },
  dot: { width: 7, height: 7, borderRadius: 999 },
  empty: { padding: "12px 14px", borderRadius: 12, border: `1px dashed ${colors.borderStrong}`, fontFamily: fonts.sans, fontSize: 12.5, color: colors.textSecondary, lineHeight: 1.4 },
  epw: { marginTop: 12, display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 12, border: "1px dashed", cursor: "pointer", transition: "background 0.2s ease, border-color 0.2s ease" },
  epwIcon: { fontSize: 16, color: colors.textSecondary },
  epwText: { fontFamily: fonts.sans, fontSize: 12.5, color: colors.textSecondary },
  epwClear: { marginLeft: "auto", flexShrink: 0, padding: "4px 12px", borderRadius: 999, border: `1px solid ${colors.pillar.resources}`, background: "#fff", color: colors.pillar.resources, fontFamily: fonts.sans, fontSize: 11.5, fontWeight: 700, cursor: "pointer" },
  mismatch: { marginTop: 12, display: "flex", alignItems: "flex-start", gap: 8, padding: "10px 12px", borderRadius: 12, background: "rgba(255,159,10,0.08)", border: `1px solid ${colors.warn}`, fontFamily: fonts.sans, fontSize: 11.5, lineHeight: 1.45, color: colors.textPrimary },
  mismatchIcon: { color: colors.warn, fontSize: 13, lineHeight: 1.2, flexShrink: 0 },
};
